/**
 * Document Preview — authenticated page at /documents/:type/:id/preview.
 *
 * Send/Export separation (2026-09-14): this is "Export PDF"'s new data
 * source. Unlike SharedDocumentViewer (public /d/:token, backed by a
 * persisted snapshot + revocable share token), this page fetches the
 * document's LIVE data straight from the caller's own company via GET
 * /api/documents/:type/:id/preview -- no document_snapshots row, no
 * share_tokens row, no email, no public link ever created. It reuses the
 * exact same renderer registry (registerDocuments.ts) and the exact same
 * "browser prints it client-side via window.print()" approach as the
 * public viewer -- the PDF itself is still produced identically, only the
 * data-fetch path changed.
 *
 * Deliberately NOT reusing SharedDocumentViewer directly: that component is
 * the public-facing surface (must stay untouched) and carries concerns
 * (anonymous viewer, revoked/expired token copy, "Open in KrewUp" deep
 * link) that don't apply here -- the caller is already inside KrewUp.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '@/lib/api-config';
import { waitForInitialSession } from '@/lib/auth-session';
import { getDocumentComponent } from './registry';
import './registerDocuments';

interface PreviewResponse {
  success: boolean;
  doc_type: string;
  doc_id: string;
  capabilities: { pdf?: boolean; print?: boolean };
  payload: any;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: PreviewResponse };

const ERROR_COPY: Record<string, string> = {
  not_found: 'This document could not be found.',
  unsupported_type: 'This document type does not support PDF export.',
  network: 'We could not load this document. Check your connection and try again.',
  internal: 'Something went wrong loading this document.',
};

export default function DocumentPreview() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const [searchParams] = useSearchParams();
  const printMode = searchParams.get('print') === '1';
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!type || !id) {
        setState({ status: 'error', message: ERROR_COPY.not_found });
        return;
      }
      try {
        // This page opens in a NEW tab, outside the app shell: the session is restored asynchronously, and a request sent before
        // it is ready goes out without the Authorization header (a 401, shown as "Something went wrong").
        await waitForInitialSession();
        const res = await fetch(getApiUrl(`api/documents/${encodeURIComponent(type)}/${encodeURIComponent(id)}/preview`));
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = body?.error || 'internal';
          if (!cancelled) setState({ status: 'error', message: ERROR_COPY[code] || ERROR_COPY.internal });
          return;
        }
        const data: PreviewResponse = await res.json();
        if (!cancelled) setState({ status: 'ready', data });
      } catch {
        if (!cancelled) setState({ status: 'error', message: ERROR_COPY.network });
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [type, id]);

  // Always opened specifically to export/print -- trigger the browser's
  // print dialog once the document has rendered (same 400ms settle delay
  // SharedDocumentViewer uses for its own print mode).
  useEffect(() => {
    if (printMode && state.status === 'ready' && getDocumentComponent(state.data.doc_type)) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [printMode, state]);

  const DocComponent = state.status === 'ready' ? getDocumentComponent(state.data.doc_type) : null;

  return (
    <div style={{ minHeight: '100vh', background: 'rgb(var(--gray-100))' }}>
      <style>{`
        @media print {
          .kw-doc-toolbar { display: none !important; }
          .kw-doc-page { box-shadow: none !important; margin: 0 !important; padding: 0 !important; max-width: none !important; }
          body { background: rgb(var(--white)) !important; }
        }
        @page { margin: 0.5in; }
      `}</style>

      <div
        className="kw-doc-toolbar"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px', background: 'rgb(var(--gray-900))', color: 'rgb(var(--white))', position: 'sticky', top: 0, zIndex: 'var(--z-sticky)' as unknown as number,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600 }}>
          <span>KrewUp</span>
          {state.status === 'ready' && (
            <span style={{ fontSize: 12, color: 'rgb(var(--gray-400))', fontWeight: 400 }}>
              {state.data.doc_type.replace(/_/g, ' ')} — preview
            </span>
          )}
        </div>
        {state.status === 'ready' && DocComponent && (
          <button
            onClick={() => window.print()}
            style={{ padding: '8px 14px', background: 'rgb(var(--info))', color: 'rgb(var(--white))', border: 'none', borderRadius: 'calc(var(--radius) - 2px)', cursor: 'pointer', fontSize: 14 }}
          >
            Export to PDF
          </button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        {state.status === 'loading' && (
          <div style={{ color: 'rgb(var(--gray-500))', marginTop: 40 }}>Loading document…</div>
        )}

        {state.status === 'error' && (
          <div style={{ maxWidth: 420, marginTop: 60, textAlign: 'center', color: 'rgb(var(--gray-700))' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <p style={{ fontSize: 15 }}>{state.message}</p>
          </div>
        )}

        {state.status === 'ready' && DocComponent === null && (
          <div style={{ maxWidth: 460, marginTop: 60, textAlign: 'center', color: 'rgb(var(--gray-700))' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <p style={{ fontSize: 15 }}>No renderer registered for this document type.</p>
          </div>
        )}

        {state.status === 'ready' && DocComponent && (
          <div
            className="kw-doc-page"
            style={{ background: 'rgb(var(--white))', padding: 40, maxWidth: 880, width: '100%', boxShadow: 'var(--shadow-doc-page)', borderRadius: 'var(--radius)' }}
          >
            <DocComponent
              payload={state.data.payload}
              docType={state.data.doc_type}
              docId={state.data.doc_id}
              mode={printMode ? 'print' : 'view'}
              capabilities={state.data.capabilities || {}}
            />
          </div>
        )}
      </div>
    </div>
  );
}
